import { TextInput } from "src/Components";
import SimpleTextInput from "src/Components/SimpleTextInput";
import { IOrderbook, IOrderbookAskBid } from "src/Interfaces/orderbook";

interface EditableOrderbookTableProps {
  tableInfo?: IOrderbook;
  onChange: (value: IOrderbook) => void;
  onRemove: () => void;
}

const MIN_ROWS = 5;

const EMPTY_ROW: IOrderbookAskBid = {
  count: 0,
  price: 0,
  volume: 0,
};

const EditableOrderbookTable = ({
  tableInfo,
  onChange,
  onRemove,
}: EditableOrderbookTableProps) => {
  const asks = tableInfo?.asks || [];
  const bids = tableInfo?.bids || [];
  const rowsCount = Math.max(MIN_ROWS, asks.length, bids.length);
  const rows = Array.from({ length: rowsCount }, (_, index) => index);

  const updateSideValue = (
    side: "asks" | "bids",
    rowIndex: number,
    field: keyof IOrderbookAskBid,
    value: number,
  ) => {
    if (!tableInfo) return;

    const nextSideValues = [...tableInfo[side]];
    while (nextSideValues.length <= rowIndex) {
      nextSideValues.push({ ...EMPTY_ROW });
    }

    nextSideValues[rowIndex] = {
      ...(nextSideValues[rowIndex] || EMPTY_ROW),
      [field]: Number.isNaN(value) ? 0 : value,
    };

    onChange({
      ...tableInfo,
      [side]: nextSideValues,
    });
  };

  const inputValue = (value?: number) =>
    value || value === 0 ? `${value}` : "";
  const nameValue = tableInfo?.name || "";

  return (
    <div className="border border-gray-300 rounded p-3 relative">
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 left-2 text-red-600 font-bold text-sm"
      >
        x
      </button>
      <TextInput
        name="name"
        value={nameValue}
        onChange={(_, value) => {
          if (!tableInfo) return;
          onChange({
            ...tableInfo,
            name: value,
          });
        }}
        label="نام"
        extraLabel
      />
      <div className="text-sm font-bold mt-3">{tableInfo?.code || "-"}</div>

      <div className="grid grid-cols-2 gap-3">
        <div className="green-border rounded overflow-hidden">
          <div className="bg-green-100 text-xs font-bold grid grid-cols-3">
            <div className="p-1 text-center">Count</div>
            <div className="p-1 text-center">Volume</div>
            <div className="p-1 text-center">Bid</div>
          </div>
          {rows.map((rowIndex) => (
            <div key={`bid-${rowIndex}`} className="grid grid-cols-3 h-8">
              <SimpleTextInput
                name={`bids-count-${rowIndex}`}
                kind="number"
                value={inputValue(bids[rowIndex]?.count)}
                onChange={(_, value) =>
                  updateSideValue("bids", rowIndex, "count", Number(value))
                }
                className="!rounded-none !border-gray-100 text-xs"
              />
              <SimpleTextInput
                name={`bids-volume-${rowIndex}`}
                kind="number"
                value={inputValue(bids[rowIndex]?.volume)}
                onChange={(_, value) =>
                  updateSideValue("bids", rowIndex, "volume", Number(value))
                }
                className="!rounded-none !border-gray-100 text-xs"
              />
              <SimpleTextInput
                name={`bids-price-${rowIndex}`}
                kind="number"
                value={inputValue(bids[rowIndex]?.price)}
                onChange={(_, value) =>
                  updateSideValue("bids", rowIndex, "price", Number(value))
                }
                className="!rounded-none !border-gray-100 text-xs"
              />
            </div>
          ))}
        </div>

        <div className="red-border rounded overflow-hidden">
          <div className="bg-red-100 text-xs font-bold grid grid-cols-3">
            <div className="p-1 text-center">Ask</div>
            <div className="p-1 text-center">Volume</div>
            <div className="p-1 text-center">Count</div>
          </div>
          {rows.map((rowIndex) => (
            <div key={`ask-${rowIndex}`} className="grid grid-cols-3 h-8">
              <SimpleTextInput
                name={`asks-price-${rowIndex}`}
                kind="number"
                value={inputValue(asks[rowIndex]?.price)}
                onChange={(_, value) =>
                  updateSideValue("asks", rowIndex, "price", Number(value))
                }
                className="!rounded-none !border-gray-100 text-xs"
              />
              <SimpleTextInput
                name={`asks-volume-${rowIndex}`}
                kind="number"
                value={inputValue(asks[rowIndex]?.volume)}
                onChange={(_, value) =>
                  updateSideValue("asks", rowIndex, "volume", Number(value))
                }
                className="!rounded-none !border-gray-100 text-xs"
              />
              <SimpleTextInput
                name={`asks-count-${rowIndex}`}
                kind="number"
                value={inputValue(asks[rowIndex]?.count)}
                onChange={(_, value) =>
                  updateSideValue("asks", rowIndex, "count", Number(value))
                }
                className="!rounded-none !border-gray-100 text-xs"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EditableOrderbookTable;
